import {
    Grid,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { SupportServer } from "../../interfaces/SupportServer";
import SupportServerService from "../../services/SupportServerService";

interface Props {
    targetServer?: SupportServer;
    openState: boolean;
    setOpenState: (show: boolean) => void;
    dialogMode: string;
}

export const SupportServerDialog = ({ targetServer, openState, setOpenState, dialogMode }: Props) => {
    const { t } = useTranslation();
    const [cookies] = useCookies(["access_token"]);

    const [showSaveButton, setShowSaveButton] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteError, setDeleteError] = useState<string>("");

    const [serverName, setServerName] = useState<string>(
        targetServer ? targetServer.server_name : ""
    );
    const [serverNameChanged, setServerNameChanged] = useState(false);

    const [serverType, setServerType] = useState<'webrtc' | 'jitsi' | 'ai' | 'sso'>(
        targetServer ? targetServer.server_type : 'webrtc'
    );
    const [serverTypeChanged, setServerTypeChanged] = useState(false);

    const [serverUrl, setServerUrl] = useState<string>(
        targetServer ? targetServer.server_url : ""
    );
    const [serverUrlChanged, setServerUrlChanged] = useState(false);

    const [authToken, setAuthToken] = useState<string>(
        targetServer ? (targetServer.auth_token || "") : ""
    );
    const [authTokenChanged, setAuthTokenChanged] = useState(false);

    const [isActive, setIsActive] = useState<boolean>(
        targetServer ? targetServer.is_active : true
    );
    const [isActiveChanged, setIsActiveChanged] = useState(false);

    const [additionalConfig, setAdditionalConfig] = useState<string>(
        targetServer && targetServer.additional_config
            ? JSON.stringify(targetServer.additional_config, null, 2)
            : ""
    );
    const [additionalConfigChanged, setAdditionalConfigChanged] = useState(false);

    const handleClose = () => {
        setOpenState(false);
        window.location.href = "/supportservers";
    };

    const handleSaveChanges = () => {
        console.log("Handle save changes:", dialogMode);

        let parsedConfig = {};
        if (additionalConfig.trim()) {
            try {
                parsedConfig = JSON.parse(additionalConfig);
            } catch (e) {
                alert("Invalid JSON in additional configuration");
                return;
            }
        }

        const data: any = {
            server_url: serverUrl,
            auth_token: authToken,
            additional_config: parsedConfig,
            is_active: isActive,
        };

        // For creates: include server_name and server_type
        // For updates: include server_id
        if (dialogMode === "add") {
            data.server_name = serverName;
            data.server_type = serverType;
        } else {
            data.server_id = targetServer?.server_id;
            data.server_name = serverName;  // Can update name
        }

        SupportServerService.manageSupportServer(data, cookies.access_token)
            .then((response) => {
                console.log("Success:", response);
                setShowSaveButton(false);
                setOpenState(false);
                window.location.href = "/supportservers";
            })
            .catch((errorResponse) => {
                console.log("Error:", errorResponse);
                alert("Failed to save server configuration: " + 
                    (errorResponse?.data?.error || "Unknown error"));
            });
    };

    const handleDelete = () => {
        if (!targetServer?.server_id) {
            console.error("No server ID available for deletion. Server data:", targetServer);
            setDeleteError("Cannot delete: Server ID is missing.");
            return;
        }

        console.log("Attempting to delete server with ID:", targetServer.server_id);
        console.log("Target server object:", targetServer);
        setDeleteError("");  // Clear previous errors

        SupportServerService.deleteSupportServer(targetServer.server_id, cookies.access_token)
            .then((response) => {
                console.log("Delete success:", response);
                // Only reload on success
                window.location.href = "/supportservers";
            })
            .catch((error) => {
                console.error("Delete error caught in dialog:", error);
                console.error("Error data:", error?.data);
                const errorMsg = error?.data?.error || error?.data?.detail || error?.message || "Failed to delete server configuration";
                setDeleteError(errorMsg);
                setConfirmDelete(false);  // Reset delete confirmation
            });
    };

    useEffect(() => {
        const itemsChanged = 
            serverNameChanged ||
            serverTypeChanged ||
            serverUrlChanged ||
            authTokenChanged ||
            isActiveChanged ||
            additionalConfigChanged;

        if (dialogMode === "add") {
            const showSave = serverName !== '' && serverUrl !== '';
            setShowSaveButton(showSave);
        } else {
            setShowSaveButton(itemsChanged);
        }
    }, [
        dialogMode,
        serverName,
        serverType,
        serverUrl,
        authToken,
        isActive,
        additionalConfig,
        serverNameChanged,
        serverTypeChanged,
        serverUrlChanged,
        authTokenChanged,
        isActiveChanged,
        additionalConfigChanged
    ]);

    const getPlaceholderConfig = () => {
        switch (serverType) {
            case 'webrtc':
                return '{\n  "channel_id": "0",\n  "quality": "hd"\n}';
            case 'jitsi':
                return '{\n  "config_overwrite": {\n    "startWithAudioMuted": true\n  }\n}';
            case 'ai':
                return '{\n  "model": "default"\n}';
            default:
                return '';
        }
    };

    return (
        <Dialog
            open={openState}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {dialogMode === "add" 
                    ? t("add_support_server") 
                    : t("edit_support_server")}
            </DialogTitle>

            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t("server_name")}
                                value={serverName}
                                onChange={(e) => {
                                    setServerName(e.target.value);
                                    setServerNameChanged(true);
                                }}
                                placeholder="Primary WebRTC Server"
                                helperText={t("server_name_can_duplicate")}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>{t("server_type")}</InputLabel>
                                <Select
                                    value={serverType}
                                    label={t("server_type")}
                                    onChange={(e) => {
                                        setServerType(e.target.value as 'webrtc' | 'jitsi' | 'ai' | 'sso');
                                        setServerTypeChanged(true);
                                    }}
                                    disabled={dialogMode === "edit"}
                                >
                                    <MenuItem value="webrtc">WebRTC Video Streaming</MenuItem>
                                    <MenuItem value="jitsi">Jitsi Video Conferencing</MenuItem>
                                    <MenuItem value="ai">AI Processing Server</MenuItem>
                                    <MenuItem value="sso">Single Sign-On (SSO)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t("server_url")}
                                value={serverUrl}
                                onChange={(e) => {
                                    setServerUrl(e.target.value);
                                    setServerUrlChanged(true);
                                }}
                                placeholder={
                                    serverType === 'jitsi' 
                                        ? "videocall.example.com" 
                                        : "https://..."
                                }
                                helperText={
                                    serverType === 'webrtc' 
                                        ? "Must start with https://" 
                                        : serverType === 'jitsi'
                                        ? "Domain only (no protocol)"
                                        : ""
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t("auth_token")}
                                value={authToken}
                                onChange={(e) => {
                                    setAuthToken(e.target.value);
                                    setAuthTokenChanged(true);
                                }}
                                placeholder={t("optional")}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                label={t("additional_config")}
                                value={additionalConfig}
                                onChange={(e) => {
                                    setAdditionalConfig(e.target.value);
                                    setAdditionalConfigChanged(true);
                                }}
                                placeholder={getPlaceholderConfig()}
                                helperText={t("json_format_optional")}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => {
                                            setIsActive(e.target.checked);
                                            setIsActiveChanged(true);
                                        }}
                                    />
                                }
                                label={t("active")}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                {deleteError && (
                    <Typography color="error" sx={{ mr: "auto", fontSize: "0.875rem" }}>
                        {deleteError}
                    </Typography>
                )}
                
                {dialogMode === "edit" && !deleteError && (
                    <>
                        {!confirmDelete ? (
                            <Button
                                onClick={() => setConfirmDelete(true)}
                                color="error"
                                sx={{ mr: "auto" }}
                            >
                                {t("delete")}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleDelete}
                                variant="contained"
                                color="error"
                                sx={{ mr: "auto" }}
                            >
                                {t("confirm_delete")}
                            </Button>
                        )}
                    </>
                )}

                <Button onClick={handleClose}>{t("cancel")}</Button>
                
                {showSaveButton && (
                    <Button
                        onClick={handleSaveChanges}
                        variant="contained"
                        color="primary"
                    >
                        {t("save")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
